import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SEO } from "@/components/seo/SEO";
import { useCommissionSettings } from "@/features/commission-settings/hooks";
import type { CommissionSettings } from "@/features/commission-settings/types";
import { PublicEmpty, PublicError, PublicLoading } from "@/pages/public/PublicState";

interface ServiceCard {
  title: string;
  description: string;
}

function enabledServices(settings: CommissionSettings): ServiceCard[] {
  return [
    settings.vehicleLiveriesEnabled
      ? {
          title: "Vehicle Liveries",
          description: "Custom vehicle designs, fleet identity, and themed livery work.",
        }
      : null,
    settings.uniformDesignsEnabled
      ? {
          title: "Uniform Designs",
          description: "Uniform concepts and polished apparel design packages.",
        }
      : null,
    settings.customDesignsEnabled
      ? {
          title: "Custom Designs",
          description: "Specialty design requests that do not fit a standard category.",
        }
      : null,
  ].filter(Boolean) as ServiceCard[];
}

function TextSection({ title, text }: { title: string; text: string | null }) {
  return (
    <CyberPanel>
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200">{title}</p>
      <p className="mt-4 whitespace-pre-line leading-8 text-content-secondary">
        {text || `${title} details are being prepared in the CMS.`}
      </p>
    </CyberPanel>
  );
}

function CtaLink({ href, children, muted }: { href: string; children: string; muted?: boolean }) {
  const classes = muted ? "w-full opacity-70 hover:opacity-100 sm:w-auto" : "w-full sm:w-auto";
  const isExternal = /^https?:\/\//i.test(href);

  if (isExternal) {
    return (
      <Button asChild variant={muted ? "secondary" : "primary"} size="lg" className={classes}>
        <a href={href} target="_blank" rel="noreferrer">
          {children}
        </a>
      </Button>
    );
  }

  return (
    <Button asChild variant={muted ? "secondary" : "primary"} size="lg" className={classes}>
      <Link to={href}>{children}</Link>
    </Button>
  );
}

export function CommissionsPage() {
  const query = useCommissionSettings();

  if (query.isLoading) return <PublicLoading label="Loading commission settings..." />;
  if (query.isError) return <PublicError label="Unable to load commission settings." />;
  if (!query.data) {
    return (
      <PublicEmpty
        title="Commission settings unavailable"
        description="The CMS has not returned commission settings yet."
      />
    );
  }

  const settings = query.data;
  const services = enabledServices(settings);
  const ctaUrl = settings.contactCtaUrl || "/contact";
  const ctaText = settings.contactCtaText || "Contact Me";

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <SEO title={settings.headline || "Commissions"} description={settings.introText || "Commission information and availability."} />
      <CyberPanel>
        <div className="flex min-w-0 flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div
              className={
                settings.isOpen
                  ? "inline-flex rounded border border-cyan-300/50 bg-cyan-300/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-cyan-100 shadow-neon"
                  : "inline-flex rounded border border-red-300/40 bg-red-500/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-red-100"
              }
            >
              {settings.isOpen ? "Commissions Open" : "Commissions Closed"}
            </div>
            <h1 className="mt-5 break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl">
              {settings.headline || "Commissions"}
            </h1>
            <p className="mt-4 max-w-3xl whitespace-pre-line text-base leading-7 text-content-secondary sm:mt-5 sm:text-lg sm:leading-8">
              {settings.introText || "Commission information is being prepared in the CMS."}
            </p>
          </div>

          <div className="w-full shrink-0 rounded-lg border border-cyan-300/20 bg-black/45 p-4 text-sm text-content-secondary lg:w-auto">
            <span className="font-bold text-white">{services.length}</span> service type{services.length === 1 ? "" : "s"} enabled
          </div>
        </div>
      </CyberPanel>

      {!settings.isOpen ? (
        <div className="mt-6 rounded-lg border border-red-300/30 bg-red-500/10 p-5 text-red-50">
          <p className="font-display text-2xl font-bold uppercase">Commissions are currently closed</p>
          <p className="mt-2 text-sm leading-6 text-red-100/80">
            You can still review services and requirements, but new requests may not be accepted right now.
          </p>
        </div>
      ) : null}

      <div className="mt-8">
        <h2 className="font-display text-2xl font-bold uppercase text-white">Services</h2>
        {services.length > 0 ? (
          <div className="mt-4 grid gap-4 md:grid-cols-3 md:gap-5">
            {services.map((service) => (
              <CyberPanel key={service.title} className="h-full">
                <div className="mb-5 h-px w-16 bg-cyan-300 shadow-neon" />
                <h3 className="break-words font-display text-xl font-bold uppercase text-white sm:text-2xl">{service.title}</h3>
                <p className="mt-4 text-sm leading-7 text-content-secondary">{service.description}</p>
              </CyberPanel>
            ))}
          </div>
        ) : (
          <CyberPanel className="mt-4">
            <p className="text-content-secondary">No commission service types are enabled in the CMS.</p>
          </CyberPanel>
        )}
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        <TextSection title="Process" text={settings.processText} />
        <TextSection title="Pricing" text={settings.pricingText} />
        <TextSection title="Turnaround" text={settings.turnaroundText} />
        <TextSection title="Requirements" text={settings.requirementsText} />
      </div>

      <CyberPanel className="mt-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200">Next Step</p>
            <h2 className="mt-3 break-words font-display text-2xl font-bold uppercase text-white sm:text-3xl">
              {settings.isOpen ? "Ready to start a commission?" : "Check back for availability"}
            </h2>
            <p className="mt-3 max-w-2xl text-content-secondary">
              {settings.isOpen
                ? "Use the commission contact link to send project details and requirements."
                : "The contact action is still available, but availability is limited while commissions are closed."}
            </p>
          </div>
          <CtaLink href={ctaUrl} muted={!settings.isOpen}>
            {settings.isOpen ? ctaText : "Commissions Closed"}
          </CtaLink>
        </div>
      </CyberPanel>
    </section>
  );
}
